/**
 * Gift Form Persistence
 * Saves and restores Recharge gift form data using sessionStorage
 * Works independently of other scripts
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'recharge_gift_form_data';

  const GiftFormPersistence = {
    // Save current form data to sessionStorage
    save: function() {
      const giftForm = document.querySelector('form[id*="rc_gifting"]');
      if (!giftForm) return;

      const data = {
        firstName: document.getElementById('rc_gift_recipient_first_name')?.value || '',
        lastName: document.getElementById('rc_gift_recipient_last_name')?.value || '',
        email: document.getElementById('rc_gift_recipient_email')?.value || '',
        message: document.getElementById('rc_gift_message')?.value || '',
        scheduledAt: document.getElementById('rc_gift_notification_scheduled_at')?.value || '',
        timestamp: Date.now()
      };

      // Only save if at least one field has data
      if (data.firstName || data.lastName || data.email || data.message || data.scheduledAt) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      }
    },

    // Restore form data from sessionStorage
    restore: function() {
      const savedData = sessionStorage.getItem(STORAGE_KEY);
      if (!savedData) return false;

      try {
        const data = JSON.parse(savedData);

        // Check if data is stale (older than 1 hour)
        if (Date.now() - data.timestamp > 3600000) {
          sessionStorage.removeItem(STORAGE_KEY);
          return false;
        }

        // Wait for form fields to exist
        const firstNameInput = document.getElementById('rc_gift_recipient_first_name');
        const lastNameInput = document.getElementById('rc_gift_recipient_last_name');
        const emailInput = document.getElementById('rc_gift_recipient_email');
        const messageInput = document.getElementById('rc_gift_message');
        const scheduledAtInput = document.getElementById('rc_gift_notification_scheduled_at');

        if (!firstNameInput || !lastNameInput || !emailInput) return false;

        // Restore values
        if (data.firstName) firstNameInput.value = data.firstName;
        if (data.lastName) lastNameInput.value = data.lastName;
        if (data.email) emailInput.value = data.email;
        if (data.message && messageInput) messageInput.value = data.message;
        if (data.scheduledAt && scheduledAtInput) scheduledAtInput.value = data.scheduledAt;

        // Trigger events to notify other scripts (like Recharge widget)
        [firstNameInput, lastNameInput, emailInput, messageInput, scheduledAtInput].forEach(input => {
          if (input && input.value) {
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        return true;
      } catch (e) {
        console.error('Failed to restore gift form data:', e);
        sessionStorage.removeItem(STORAGE_KEY);
        return false;
      }
    },

    // Set up auto-save on field changes
    setupAutoSave: function() {
      const fieldIds = [
        'rc_gift_recipient_first_name',
        'rc_gift_recipient_last_name',
        'rc_gift_recipient_email',
        'rc_gift_message',
        'rc_gift_notification_scheduled_at'
      ];

      fieldIds.forEach(id => {
        const input = document.getElementById(id);
        if (input) {
          input.addEventListener('input', () => this.save());
          input.addEventListener('change', () => this.save());
        }
      });
    },

    // Wait for gift form to appear, then restore and setup auto-save
    initialize: function() {
      const giftForm = document.querySelector('form[id*="rc_gifting"]');

      if (giftForm) {
        // Form already exists, restore and setup
        this.restore();
        this.setupAutoSave();
      } else {
        // Wait for form to appear using observer
        const observer = new MutationObserver((mutations) => {
          const relevantChange = mutations.some(mutation => {
            return Array.from(mutation.addedNodes).some(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                return node.matches?.('form[id*="rc_gifting"]') ||
                       node.querySelector?.('form[id*="rc_gifting"]');
              }
            });
          });

          if (relevantChange) {
            observer.disconnect();

            // Give Recharge widget time to initialize before restoring
            setTimeout(() => {
              this.restore();
              this.setupAutoSave();
            }, 500);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        // Stop observing after 10 seconds (non-gift product)
        setTimeout(() => observer.disconnect(), 10000);
      }
    },

    // Clear saved data
    clear: function() {
      sessionStorage.removeItem(STORAGE_KEY);

      // Also clear the actual form fields
      const firstNameInput = document.getElementById('rc_gift_recipient_first_name');
      const lastNameInput = document.getElementById('rc_gift_recipient_last_name');
      const emailInput = document.getElementById('rc_gift_recipient_email');
      const messageInput = document.getElementById('rc_gift_message');
      const scheduledAtInput = document.getElementById('rc_gift_notification_scheduled_at');

      [firstNameInput, lastNameInput, emailInput, messageInput, scheduledAtInput].forEach(input => {
        if (input) {
          input.value = '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => GiftFormPersistence.initialize());
  } else {
    GiftFormPersistence.initialize();
  }

  // Re-initialize after AJAX product changes
  window.addEventListener('product:loaded', () => {
    setTimeout(() => GiftFormPersistence.initialize(), 200);
  });

  // Save before page unload
  window.addEventListener('beforeunload', () => GiftFormPersistence.save());

  // Add gift data as hidden inputs to the product form for line item properties
  const addGiftDataToProductForm = function() {
    const giftForm = document.querySelector('form[id*="rc_gifting"]');
    if (!giftForm) {
      console.log('Gift form properties: No gift form found');
      return;
    }

    // Find the main product form - try multiple selectors
    let productForm = document.querySelector('form[id^="product-form-"]');
    if (!productForm) {
      productForm = document.querySelector('form.main-product-form');
    }
    if (!productForm) {
      productForm = document.querySelector('form[action*="/cart/add"]');
    }
    if (!productForm) {
      console.log('Gift form properties: No product form found');
      return;
    }

    console.log('Found product form:', productForm.id);

    // Get current gift form values
    const firstName = document.getElementById('rc_gift_recipient_first_name')?.value || '';
    const lastName = document.getElementById('rc_gift_recipient_last_name')?.value || '';
    const email = document.getElementById('rc_gift_recipient_email')?.value || '';
    const message = document.getElementById('rc_gift_message')?.value || '';
    const scheduledAt = document.getElementById('rc_gift_notification_scheduled_at')?.value || '';

    // Only add properties if at least the required fields are filled
    if (!firstName || !lastName || !email) {
      console.log('Gift form properties: Required fields not filled');
      return;
    }

    // Remove any existing gift property inputs
    productForm.querySelectorAll('input[name^="properties[Backup"]').forEach(input => input.remove());

    // Add hidden inputs for line item properties (no underscore so they're visible in admin)
    const properties = {
      'Backup Gift To': `${firstName} ${lastName}`,
      'Backup Gift Email': email
    };

    if (message) {
      properties['Backup Gift Note'] = message;
    }

    if (scheduledAt) {
      properties['Backup Gift Send Date'] = scheduledAt;
    }

    console.log('Adding gift properties to product form:', properties);

    // Create and append hidden inputs
    Object.entries(properties).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value;
      productForm.appendChild(input);
    });

    console.log('Gift properties added to form:', productForm.id);
  };

  // Update line item properties when gift form fields change
  const setupPropertySync = function() {
    const fieldIds = [
      'rc_gift_recipient_first_name',
      'rc_gift_recipient_last_name',
      'rc_gift_recipient_email',
      'rc_gift_message',
      'rc_gift_notification_scheduled_at'
    ];

    fieldIds.forEach(id => {
      const input = document.getElementById(id);
      if (input) {
        input.addEventListener('input', addGiftDataToProductForm);
        input.addEventListener('change', addGiftDataToProductForm);
      }
    });

    // Add initial properties
    addGiftDataToProductForm();

    // Also add properties right before form submission to ensure they're there
    const productForm = document.querySelector('form[id^="product-form-"]') ||
                        document.querySelector('form.main-product-form');

    if (productForm) {
      console.log('Setting up form submit listener on:', productForm.id);

      // Use capture phase to run before other handlers
      productForm.addEventListener('submit', function(e) {
        console.log('Form submitting, adding properties now...');
        // Add properties one more time right before submission
        addGiftDataToProductForm();

        // Small delay to ensure properties are in DOM before form submits
        // e.preventDefault();
        // setTimeout(() => {
        //   productForm.submit();
        // }, 10);
      }, { capture: true });
    }
  };

  // Initialize property sync when gift form is ready
  window.addEventListener('product:loaded', () => {
    setTimeout(() => {
      const giftForm = document.querySelector('form[id*="rc_gifting"]');
      if (giftForm) {
        setupPropertySync();
      }
    }, 600);
  });

  // Also initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        const giftForm = document.querySelector('form[id*="rc_gifting"]');
        if (giftForm) {
          setupPropertySync();
        }
      }, 1000);
    });
  } else {
    setTimeout(() => {
      const giftForm = document.querySelector('form[id*="rc_gifting"]');
      if (giftForm) {
        setupPropertySync();
      }
    }, 1000);
  }

  // Clear storage after successful add to cart
  document.addEventListener('cart:item-added', () => {
    console.log('Gift form: Cart item added, clearing saved data');
    GiftFormPersistence.clear();
  });

  // Also listen for Shopify's cart add event
  document.addEventListener('product:added-to-cart', () => {
    console.log('Gift form: Product added to cart, clearing saved data');
    GiftFormPersistence.clear();
  });

  // Listen for form submission on product forms to clear after add to cart
  const observeProductFormSubmission = () => {
    const productFormElement = document.querySelector('product-form');
    if (productFormElement) {
      const productForm = productFormElement.querySelector('form[id^="product-form-"]');
      if (productForm) {
        productForm.addEventListener('submit', () => {
          // Wait a bit to ensure add to cart succeeded before clearing
          setTimeout(() => {
            console.log('Gift form: Product form submitted, clearing saved data');
            GiftFormPersistence.clear();
          }, 1000);
        });
      }
    }
  };

  // Set up form submission listener
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeProductFormSubmission);
  } else {
    observeProductFormSubmission();
  }

  // Re-observe after AJAX product changes
  window.addEventListener('product:loaded', observeProductFormSubmission);

  // Expose to window for manual control if needed
  window.GiftFormPersistence = GiftFormPersistence;

})();
